// useKidBattle — kid-side battle state. Tracks the kid's active
// fight (single battle at a time, server-enforced) and exposes
// start / attack(cardType) / flee actions that wrap the RPCs.
//
// Realtime: subscribes to kid_battles changes scoped to this kid so
// the UI updates the moment the server applies damage + transitions
// status to won/lost.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

export function useKidBattle(kidUserId) {
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const chRef = useRef(null);

  const loadActive = useCallback(async () => {
    if (!kidUserId) { setActive(null); return; }
    const { data, error: err } = await supabase
      .from("kid_battles")
      .select("*")
      .eq("kid_user_id", kidUserId)
      .eq("status", "active")
      .order("started_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (err) {
      console.warn("[kid-battle] load:", err.message);
      return;
    }
    setActive(data || null);
  }, [kidUserId]);

  useEffect(() => { loadActive(); }, [loadActive]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:kid_battles:${kidUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_battles", filter: `kid_user_id=eq.${kidUserId}` },
        () => loadActive())
      .subscribe();
    chRef.current = ch;
    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
    };
  }, [kidUserId, loadActive]);

  const start = useCallback(async (monsterSlug) => {
    if (!kidUserId || !monsterSlug) return null;
    setBusy(true); setError(null);
    try {
      const { data, error: err } = await supabase.rpc("start_kid_battle", {
        p_kid_user_id: kidUserId,
        p_monster_slug: monsterSlug,
      });
      if (err) throw err;
      setActive(data || null);
      return data;
    } catch (e) {
      console.warn("[kid-battle] start:", e?.message || e);
      setError(e?.message || "couldn't start battle");
      return null;
    } finally {
      setBusy(false);
    }
  }, [kidUserId]);

  const attack = useCallback(async (cardType) => {
    if (!active?.id || !cardType) return null;
    setBusy(true); setError(null);
    try {
      const { data, error: err } = await supabase.rpc("kid_battle_attack", {
        p_battle_id: active.id,
        p_card_type: cardType,
      });
      if (err) throw err;
      setActive(data || null);
      return data;
    } catch (e) {
      console.warn("[kid-battle] attack:", e?.message || e);
      setError(e?.message || "attack failed");
      return null;
    } finally {
      setBusy(false);
    }
  }, [active?.id]);

  const flee = useCallback(async () => {
    if (!active?.id) return null;
    setBusy(true); setError(null);
    try {
      const { data, error: err } = await supabase.rpc("kid_battle_flee", {
        p_battle_id: active.id,
      });
      if (err) throw err;
      setActive(null);
      return data;
    } catch (e) {
      console.warn("[kid-battle] flee:", e?.message || e);
      setError(e?.message || "couldn't flee");
      return null;
    } finally {
      setBusy(false);
    }
  }, [active?.id]);

  return { active, busy, error, start, attack, flee, refresh: loadActive };
}
